import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdDelete, MdOutlineModeEdit, MdRemoveRedEye } from "react-icons/md";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import PopUpForm from "../../components/PopUpForm";
import { localizedTextDefaultValue } from "../../constants/localizedTextDefaultValue";
import { useApi } from "../../services/api";
import { LocalizedText, Service } from "../../services/generated/client";
import {
  createService,
  getAllServices,
  removeService,
  updateService,
} from "../../services/service/serviceServices";
import styles from "./styles.module.css";

const Services = () => {
  const { withErrorHandling } = useApi();
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service>({
    _id: {},
    description: localizedTextDefaultValue,
    name: localizedTextDefaultValue,
    header1: localizedTextDefaultValue,
    header2: localizedTextDefaultValue,
    paragraph1: localizedTextDefaultValue,
    paragraph2: localizedTextDefaultValue,
    ServiceAccomplishmentCards: [],
    imageUrls: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [view, setView] = useState(false);
  const [languageEditing, setLanguageEditing] =
    useState<keyof LocalizedText>("ka");

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen]);

  useEffect(() => {
    withErrorHandling(async () => {
      const response = await getAllServices();
      if (response) setServices(response);
    });
  }, []);

  const handleEdit = (service: Service) => {
    setSelectedService(service);
    setIsAdding(false);
    setView(false);
    setIsModalOpen(true);
  };

  const handleView = (service: Service) => {
    setSelectedService(service);
    setIsAdding(false);
    setView(true);
    setIsModalOpen(true);
  };

  const handleDelete = useCallback(
    async (id: string, name: string) => {
      if (window.confirm(`Are you sure you want to delete ${name}`)) {
        await withErrorHandling(async () => {
          await removeService(id);
          setServices((prev) =>
            prev.filter((service) => service._id.toString() !== id)
          );
        });
      }
    },
    [withErrorHandling]
  );

  const handleUpdateService = async (e: FormEvent) => {
    e.preventDefault();

    const {
      _id,
      name,
      description,
      header1,
      header2,
      paragraph1,
      paragraph2,
      ServiceAccomplishmentCards,
      imageUrls,
    } = selectedService;

    const cleanedImageUrls = imageUrls.filter((url) => url.trim() !== "");

    await withErrorHandling(async () => {
      if (isAdding) {
        const newService = await createService({
          name,
          description,
          header1,
          header2,
          paragraph1,
          paragraph2,
          ServiceAccomplishmentCards,
          imageUrls: cleanedImageUrls,
        });
        setServices((prev) => [...prev, newService]);
      } else if (!view) {
        await updateService(_id.toString(), {
          name,
          description,
          header1,
          header2,
          paragraph1,
          paragraph2,
          ServiceAccomplishmentCards,
          imageUrls: cleanedImageUrls,
        });
        setServices((prev) =>
          prev.map((service) =>
            service._id === _id ? selectedService : service
          )
        );
      }
      setIsModalOpen(false);
    });
  };

  const handleAddService = () => {
    setSelectedService({
      _id: {},
      name: localizedTextDefaultValue,
      description: localizedTextDefaultValue,
      header1: localizedTextDefaultValue,
      header2: localizedTextDefaultValue,
      paragraph1: localizedTextDefaultValue,
      paragraph2: localizedTextDefaultValue,
      ServiceAccomplishmentCards: [],
      imageUrls: [],
    });
    setIsAdding(true);
    setIsModalOpen(true);
  };

  const columns = useMemo<MRT_ColumnDef<Service>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row }) => row.original.name?.[languageEditing] || "",
      },
      {
        accessorKey: "description",
        header: "Description",
        Cell: ({ row }) => row.original.description?.[languageEditing] || "",
      },
      {
        header: "Actions",
        Cell: ({ row }: { row: { original: Service } }) => {
          return (
            <div className={styles.actionBtns}>
              <button
                onClick={() => handleView(row.original)}
                className={styles.tableBtn}
              >
                <MdRemoveRedEye />
              </button>
              <button
                onClick={() => handleEdit(row.original)}
                className={styles.tableBtn}
              >
                <MdOutlineModeEdit />
              </button>
              <button
                onClick={() =>
                  handleDelete(
                    row.original._id.toString(),
                    row.original.name.en
                  )
                }
                className={`${styles.tableBtn} ${styles.delete}`}
              >
                <MdDelete />
              </button>
            </div>
          );
        },
      },
    ],
    [handleDelete, languageEditing]
  );

  const handleMultiLanguageInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id as keyof Service;
    const value = e.target.value;

    setSelectedService((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] as LocalizedText),
        [languageEditing]: value,
      },
    }));
  };

  return (
    <div className={styles.mainContent}>
      <h2>Manage Services</h2>
      <div className={styles.tableWrapper}>
        <MaterialReactTable
          columns={columns}
          data={Array.isArray(services) ? services : []}
          renderTopToolbarCustomActions={() => (
            <div style={{ display: "flex", gap: "20px" }}>
              <button className={styles.addBtn} onClick={handleAddService}>
                + Add Service
              </button>
              <div>
                <LanguageSwitcher
                  languageEditing={languageEditing}
                  setLanguageEditing={setLanguageEditing}
                  inForm={false}
                />
              </div>
            </div>
          )}
          muiTableContainerProps={{
            sx: {
              flex: 1,
              overflowY: "hidden",
            },
          }}
          muiTablePaperProps={{
            sx: {
              flex: 1,
              display: "flex",
              flexDirection: "column",
            },
          }}
          muiTableBodyProps={{
            sx: {
              overflowY: "auto",
              height: "100%",
            },
          }}
          muiTableProps={{
            sx: {
              maxHeight: "100%",
              gridTemplateRows: "auto 1fr",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              overflow: "auto",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          layoutMode="grid"
        />
      </div>

      {isModalOpen && selectedService && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} ref={modalRef}>
            <button
              className={styles.closeButton}
              onClick={() => setIsModalOpen(false)}
            >
              X
            </button>

            <LanguageSwitcher
              languageEditing={languageEditing}
              setLanguageEditing={setLanguageEditing}
              inForm={true}
            />
            <PopUpForm
              title={"Serivce"}
              submitLabel={isAdding ? "Create" : "Update"}
              onSubmit={handleUpdateService}
              isView={view}
              languageEditing={languageEditing}
              isAdding={isAdding}
              fields={[
                {
                  id: "name",
                  label: `Name ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedService?.name?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "description",
                  label: `description ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedService?.description?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "header1",
                  label: `Header 1 ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedService?.header1?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "header2",
                  label: `Header 2 ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedService?.header2?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "paragraph1",
                  label: `Paragraph 1 ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedService?.paragraph1?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "paragraph2",
                  label: `Paragraph 2 ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedService?.paragraph2?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "imageUrls",
                  label: "Image URL",
                  type: "imageUrls",
                  values: selectedService?.imageUrls || [],
                  onImageChange: (index, value) => {
                    setSelectedService((prev) => {
                      const updatedImageUrls = [...prev.imageUrls];
                      updatedImageUrls[index] = value;
                      return { ...prev, imageUrls: updatedImageUrls };
                    });
                  },
                  onAddImage: () => {
                    setSelectedService((prev) => ({
                      ...prev,
                      imageUrls: [...prev.imageUrls, ""],
                    }));
                  },
                  onRemoveImage: (index) => {
                    setSelectedService((prev) => {
                      const updatedImageUrls = [...prev.imageUrls];
                      updatedImageUrls.splice(index, 1);
                      return { ...prev, imageUrls: updatedImageUrls };
                    });
                  },
                },
                {
                  id: "ServiceAccomplishmentCards",
                  label: "Service Accomplishment Cards",
                  type: "accomplishments",
                  accomplishments:
                    selectedService?.ServiceAccomplishmentCards || [],
                  onAccomplishmentChange: (index, key, value) =>
                    setSelectedService((prev) => {
                      const updated = [
                        ...(prev.ServiceAccomplishmentCards || []),
                      ];
                      updated[index] = { ...updated[index], [key]: value };
                      return { ...prev, ServiceAccomplishmentCards: updated };
                    }),
                  onAddAccomplishment: () =>
                    setSelectedService((prev) => {
                      return {
                        ...prev,
                        ServiceAccomplishmentCards: [
                          ...prev.ServiceAccomplishmentCards,
                          { number: "", header: "", paragraph: "" },
                        ],
                      } as Service;
                    }),
                  onRemoveAccomplishment: (index) =>
                    setSelectedService((prev) => {
                      const updated = [
                        ...(prev.ServiceAccomplishmentCards || []),
                      ];
                      updated.splice(index, 1);
                      return { ...prev, ServiceAccomplishmentCards: updated };
                    }),
                },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
